import { isFieldRequired } from '@/form/zod.utils';
import { memo, PropsWithChildren } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormSchema } from '../../hooks/useFormSchema';
import RHFControlledInput, {
  OmitControlControlledInputProps,
} from '../RHFControlledInput';

export type InputsMapperField<FV extends FieldValues> =
  OmitControlControlledInputProps<FV>;

type InputsMapperProps<FV extends FieldValues> = PropsWithChildren<{
  fields: InputsMapperField<FV>[];
}>;

const InputsMapper = <FV extends FieldValues>(props: InputsMapperProps<FV>) => {
  const { fields } = props;
  const { control } = useFormContext<FV>();
  const { t, i18n } = useTranslation();

  const formSchema = useFormSchema();

  return (
    <>
      {fields.map(field => {
        const { name, label, required, ...rest } = field;

        const zodRequired = isFieldRequired(formSchema, name);

        const labelComponent =
          label === '' ? <></> : label || (i18n.exists(name) ? t(name) : label);

        return (
          <RHFControlledInput
            {...rest}
            key={name}
            name={name}
            control={control}
            required={zodRequired || required}
            label={labelComponent}
          />
        );
      })}
    </>
  );
};

export default memo(InputsMapper) as typeof InputsMapper;
